import React from "react";
import { Select } from "../../../../ui/components/Select";
import { ToggleGroup } from "../../../../ui/components/ToggleGroup";
import { DATE_PRESETS, DatePresetId } from "../config/dateRanges";

export type ReportBasis = "accrual" | "cash";

export interface ReportFilters {
  preset: DatePresetId;
  from: string;
  to: string;
  basis: ReportBasis;
}

interface ReportFilterBarProps {
  filters: ReportFilters;
  /** Balance Sheet / Trial Balance: a single "As of" date. */
  asOf: boolean;
  /** Cash Flow has no basis toggle. */
  hasBasis: boolean;
  disabled?: boolean;
  onChange: (next: ReportFilters) => void;
}

/**
 * Zoho's filter bar: Date range (14 presets + custom) and Report basis.
 * No Compare-with: Zoho's reports API rejects every `compare_with` value over
 * OAuth (probed 2026-08-28), so the control was dropped (follow-up 3).
 */
export const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  filters,
  asOf,
  hasBasis,
  disabled = false,
  onChange,
}) => {
  const custom = filters.preset === "CustomDate";

  return (
    <div className="flex w-full flex-wrap items-end gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
      <Select
        className="w-48"
        label={asOf ? "As of" : "Date range"}
        placeholder="Select"
        helpText=""
        disabled={disabled}
        value={filters.preset}
        onValueChange={(value) => onChange({ ...filters, preset: value as DatePresetId })}
      >
        {DATE_PRESETS.map((p) => (
          <Select.Item key={p.id} value={p.id}>
            {p.label}
          </Select.Item>
        ))}
      </Select>

      {custom && !asOf && (
        <label className="flex flex-col gap-1 text-caption font-caption text-subtext-color">
          From
          <input
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            disabled={disabled}
            onChange={(e) => onChange({ ...filters, from: e.target.value })}
            className="h-8 rounded-md border border-solid border-neutral-border bg-default-background px-2 text-body font-body text-default-font"
          />
        </label>
      )}
      {custom && (
        <label className="flex flex-col gap-1 text-caption font-caption text-subtext-color">
          {asOf ? "Date" : "To"}
          <input
            type="date"
            value={filters.to}
            min={!asOf && filters.from ? filters.from : undefined}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...filters, to: e.target.value, from: asOf ? e.target.value : filters.from })
            }
            className="h-8 rounded-md border border-solid border-neutral-border bg-default-background px-2 text-body font-body text-default-font"
          />
        </label>
      )}

      {hasBasis && (
        <div className="flex flex-col gap-1">
          <span className="text-caption font-caption text-subtext-color">Report basis</span>
          <ToggleGroup
            value={filters.basis}
            onValueChange={(value) => value && onChange({ ...filters, basis: value as ReportBasis })}
          >
            <ToggleGroup.Item icon={null} value="accrual" disabled={disabled}>
              Accrual
            </ToggleGroup.Item>
            <ToggleGroup.Item icon={null} value="cash" disabled={disabled}>
              Cash
            </ToggleGroup.Item>
          </ToggleGroup>
        </div>
      )}

    </div>
  );
};
